import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { UserIcon, SettingsIcon, BrainIcon, RocketIcon } from "lucide-react";
import { usePermissions } from "../../PermissionsProvider";
import { Application } from "../../model_gen/application";
import { ApplicationService } from "../../services/ApplicationService";

const ForgeOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<string>("");

  useEffect(() => {
    if (apiKey) {
      loadApplications();
    }
  }, [apiKey]);

  const loadApplications = () => {
    ApplicationService.getApplications(() => {}, apiKey).subscribe(
      (response: any) => {
        // Handle both direct array and wrapped response formats
        const apps = Array.isArray(response) ? response : (response.applications || []);
        setApplications(apps);
        if (apps.length > 0 && !selectedApplication) {
          setSelectedApplication(apps[0].id.toString());
        }
      },
      (error: any) => {
        console.error("Error loading applications:", error);
      }
    );
  };

  const handleManageAgents = () => {
    if (selectedApplication) {
      navigate(`/application/${selectedApplication}/agents`);
    }
  };

  const handleBuildComposition = () => {
    navigate("/build/compositions");
  };

  const handleJoinWaitlist = () => {
    // TODO: Implement waitlist functionality
    console.log("Join waitlist for Deploy Fleets");
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Forge</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Forge
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Build and customize your AI models, flows, and agents with advanced tools
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tool Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Finetune Models Card */}
          <Card className="border-maitai-green/20 hover:border-maitai-green/40 transition-colors flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BrainIcon className="w-5 h-5 text-maitai-green" />
                Finetune Models
              </CardTitle>
              <CardDescription>
                Build compositions of datasets and finetune your own models
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <p className="text-sm text-muted-foreground mb-4 flex-1">
                Transform your data into specialized AI models. Combine datasets and fine-tune parameters to create models that understand your unique requirements.
              </p>
              
              <Button 
                onClick={handleBuildComposition}
                className="w-full"
              >
                <BrainIcon className="w-4 h-4 mr-2" />
                Build Composition
              </Button>
            </CardContent>
          </Card>

          {/* Define Agents Card */}
          <Card className="border-maitai-green/20 hover:border-maitai-green/40 transition-colors flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-maitai-green" />
                Define Agents
              </CardTitle>
              <CardDescription>
                Orchestrate intents that work together to handle complex workflows
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <p className="text-sm text-muted-foreground mb-4">
                Group related intents into intelligent agents. Design workflows that handle multi-step tasks seamlessly.
              </p>
              
              <div className="space-y-3 flex-1 flex flex-col">
                <div>
                  <label className="text-sm font-gc-med text-foreground mb-2 block">
                    Select Application
                  </label>
                  <Select value={selectedApplication} onValueChange={setSelectedApplication}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose an application" />
                    </SelectTrigger>
                    <SelectContent>
                      {applications.map((app) => (
                        <SelectItem key={app.id} value={app.id.toString()}>
                          {app.application_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-auto">
                  <Button 
                    onClick={handleManageAgents}
                    disabled={!selectedApplication}
                    className="w-full"
                  >
                    <SettingsIcon className="w-4 h-4 mr-2" />
                    Manage Agents
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Deploy Fleets Card */}
          <Card className="border-border hover:border-muted-foreground/20 transition-colors opacity-75 flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RocketIcon className="w-5 h-5 text-muted-foreground" />
                Deploy Fleets
                <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                  Coming Soon
                </span>
              </CardTitle>
              <CardDescription>
                Orchestrate auto-scaling groups of agents to accomplish complex objectives
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <p className="text-sm text-muted-foreground mb-4 flex-1">
                Scale AI coordination to the next level. Deploy agent fleets that automatically adapt and collaborate on enterprise-scale challenges.
              </p>
              
              <Button 
                onClick={handleJoinWaitlist}
                variant="outline"
                className="w-full"
              >
                <RocketIcon className="w-4 h-4 mr-2" />
                Join Waitlist
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Getting Started Section */}
        <Card className="border-border">
          <CardContent className="p-8">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-gc-demi-bold text-foreground mb-2">
                  Getting Started with Forge
                </h2>
                <p className="text-muted-foreground font-gc-reg">
                  Learn how to use Forge to build and customize your AI models
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-border hover:border-muted-foreground/20 transition-colors">
                  <CardHeader>
                    <CardTitle className="text-lg">Documentation</CardTitle>
                    <CardDescription>
                      Comprehensive guides and API references for building with Forge
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="link" className="p-0 h-auto text-maitai-green hover:text-maitai-green/80">
                      View Documentation →
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-border hover:border-muted-foreground/20 transition-colors">
                  <CardHeader>
                    <CardTitle className="text-lg">Examples</CardTitle>
                    <CardDescription>
                      Sample projects and templates to get you started quickly
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="link" className="p-0 h-auto text-maitai-green hover:text-maitai-green/80">
                      Browse Examples →
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgeOverview; 